import config from '@config';

import { observabilityDb } from '@shared/observability';
import { resolveDocument } from '@shared/observability';
import { getMemoryFallbackEvents } from '@shared/observability';
import type { ObservabilityEventRow } from '@shared/observability';

declare const __TEMPLATE__: string;
declare const __SW_VERSION__: string;

/**
 * The same snapshot logic backs both the zip export (`export-debug-zip.ts`)
 * and the live "Canvasflow Debug" devtools panel -- a single source of truth
 * for "what does a debug session look like", so the two surfaces can never
 * silently disagree about what a reader is looking at.
 */
export function buildMeta() {
	return {
		appKey: config.build.appKey,
		template: __TEMPLATE__,
		// The tenant's own template block (`{ type, config }`) -- the feature
		// flags this build actually ran with (skipTOC, feed, sections,
		// preview, featureArticles, ...). Explains a whole class of
		// "works for me" reports that are really "that tenant has a flag set".
		//
		// Deliberately `config.app.template` and not `config.app`: one level
		// up carries `password.value` (the tenant's gate password, in
		// plaintext) and `auth.config.clientId`, and `analytics` alongside it
		// carries tracker IDs and the Segment writeKey. None of that belongs
		// in a file that leaves the device.
		templateConfig: config.app?.template,
		version: config.build.version,
		buildNumber: config.build.buildNumber,
		swVersion: typeof __SW_VERSION__ !== 'undefined' ? __SW_VERSION__ : 'dev',
		userAgent: navigator.userAgent,
		language: navigator.language,
		online: navigator.onLine,
		url: window.location.href,
	};
}

// query_error rows store a documentHash, not the document text (interned at
// flush time -- see storage/intern-document.ts). Resolved back here so the
// result is self-contained and replayable without also needing the
// documents table.
async function resolvePayload(
	row: ObservabilityEventRow,
): Promise<Record<string, unknown>> {
	if (
		row.name === 'query_error' &&
		typeof row.payload.documentHash === 'string'
	) {
		const document = await resolveDocument(row.payload.documentHash);
		const { documentHash: _documentHash, ...rest } = row.payload;
		return { ...rest, document: document ?? '<document not found>' };
	}
	return row.payload;
}

export async function buildEventsSnapshot() {
	const dbRows = await observabilityDb.events.toArray();
	const resolved = await Promise.all(
		dbRows.map(async (row) => ({
			...row,
			payload: await resolvePayload(row),
		})),
	);
	return {
		// Both tiers, deliberately -- reading only the Dexie-backed rows
		// misses exactly the failures most worth capturing: the ones that
		// happened while IndexedDB itself was unusable. See
		// local-reporter.ts's getMemoryFallbackEvents().
		db: resolved,
		memoryFallback: getMemoryFallbackEvents(),
	};
}
